import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import React, { useCallback, useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { trigger } from 'swr';
import { Box, Text } from 'rebass';

import NewMediaPopup from 'story-media/new';
import Modal from '../../../app/modal/v2';
import { postExternalMedium } from 'story-media/api';
import { useCurrentCollectionTag } from 'collections/hooks/index';
import { useNewStoryVisible, useCreateStory, useNewStoryRequestVisible } from 'stories/hooks';
import { usePostNewMeeting } from 'chats/hooks';
import { CurrentCollectionContext } from '../../../collections/current-collection-context';
import { addStoryTag } from '../../api';
import { createMediaForFiles, createTextMedium } from '../../../story-media/actions';
import { toggleNewStoryRequestVisible, toggleNewStoryVisible } from '../../actions';
import AnimatedWrap from '../../../app/modal/v2/animated-wrap';
import NewStoryRequest from 'collections/detail/story-requests/new/new-story-request';
import { getJournalIdFromPath } from 'stories/helper';
import { useFFmpeg } from 'contexts/FFmpegContext';

const GlobalNewStory = () => {

  const { pathname } = useLocation();
  const locationParams = pathname.split('/')
  const currentchapterId = locationParams[4]

  const newStoryVisible = useNewStoryVisible()
  const journalId = getJournalIdFromPath(locationParams)
  const [isValidating, setIsValidating] = useState(false);

  const isFirstTimeUser = pathname?.includes('/lets-begin')

  const newStoryRequestVisible = useNewStoryRequestVisible();

  const newStoryTag = useCurrentCollectionTag();
  const { push } = useHistory();
  const dispatch = useDispatch();
  const { currentCollection } = useContext(CurrentCollectionContext);
  const postNewMeeting = usePostNewMeeting();
  const { ffmpeg } = useFFmpeg();

  const submitText = (storyId, values) => dispatch(createTextMedium(storyId, values));
  const submitMedia = (storyId, files, position, transcribe) =>
    dispatch(createMediaForFiles(storyId, files, position, false, transcribe, undefined, ffmpeg));
  const getStoryIdFromPayload = payload => get(payload, 'data.result.stories[0]', '');

  const createNewStory = useCreateStory(currentchapterId);
  const closePopup = useCallback(() => {
    dispatch(toggleNewStoryVisible(false));
  }, [dispatch]);

  return (
    <AnimatedWrap on={newStoryVisible || newStoryRequestVisible || isFirstTimeUser} posedKey="story-global">
      {newStoryRequestVisible && (
        <Modal closeButton onClose={() => dispatch(toggleNewStoryRequestVisible(false))} aboveTooltip>
          <Box p={5}>
            <NewStoryRequest />
          </Box>
        </Modal>
      )}
      {newStoryVisible && (
        <NewMediaPopup
          isValidating={isValidating}
          title={<>
            Story<br />
            <Text as="span" fontSize="16px" color="#64748B" fontFamily="Poppins">Add content to a story</Text>
          </>}
          onRecordChat={async () => {
            setIsValidating(true)
            const [payload, newStoryId] = await postNewMeeting();
            setIsValidating(false)
            push(`${pathname?.includes('tell_a_story') ? '/tell_a_story' : '/stories'}/new/${newStoryId}/chats/${payload.data.result.meetings[0]}`);
            closePopup();
          }}
          onSubmitText={({ text }) => {
            setIsValidating(true)
            let storyId;
            return createNewStory()
              .then(async ({ payload }) => {
                storyId = getStoryIdFromPayload(payload);
                if (!storyId) return false;
                if (newStoryTag) {
                  await addStoryTag({
                    journalId: currentCollection.id,
                    storyId,
                    name: newStoryTag,
                  });
                }

                return submitText(storyId, { text, position: 0 });
              })
              .then(() => {
                setIsValidating(false)
                trigger('/v3/my/chronicle_collection');
                closePopup();
                push(`${pathname?.includes('tell_a_story') ? '/tell_a_story' : '/stories'}/new/${storyId}`);
              });
          }}
          onSubmitMedia={async (files, transcribe = false) => {
            let storyId;
            setIsValidating(true)

            return createNewStory()
              .then(async ({ payload }) => {
                storyId = getStoryIdFromPayload(payload);
                if (!storyId) return false;
                if (newStoryTag) {
                  await addStoryTag({
                    journalId: currentCollection.id,
                    storyId,
                    name: newStoryTag,
                  });
                }

                return submitMedia(storyId, files, 0, transcribe);
              })
              .then(() => {
                setIsValidating(false)
                trigger('/v3/my/chronicle_collection');
                closePopup();
                push(`${pathname?.includes('tell_a_story') ? '/tell_a_story' : '/stories'}/new/${storyId}`);
              });
          }}
          onSubmitExternalMedium={({ externalURL }) => {
            let storyId;
            setIsValidating(true)
            return createNewStory()
              .then(async ({ payload }) => {
                storyId = getStoryIdFromPayload(payload);
                if (!storyId) return false;
                if (newStoryTag) {
                  await addStoryTag({
                    journalId: currentCollection.id,
                    storyId,
                    name: newStoryTag,
                  });
                }

                return postExternalMedium({ externalURL, storyId, position: 0 });
              })
              .then(() => {
                setIsValidating(false)
                trigger('/v3/my/chronicle_collection');
                closePopup();
                push(`${pathname?.includes('tell_a_story') ? '/tell_a_story' : '/stories'}/new/${storyId}`);
              });
          }}
          onClose={closePopup}
        />
      )}
      {/* {(!newStoryVisible && isFirstTimeUser) && (
        <LetsBegin
          onClose={() => {
            push(`/child-journals/${journalId}`)
          }}
        />
      )} */}
    </AnimatedWrap>
  );
};

export default GlobalNewStory;
